import {
  Component,
  EventEmitter,
  input,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges
} from '@angular/core'
import { FormControl, ValidatorFn } from '@angular/forms'
import { Subscription } from 'rxjs'

import { CommonModule } from '@angular/common'
import { ReactiveFormsModule } from '@angular/forms'
import { GroupFieldDropdownComponent } from '../../../../shared/components/group-field-dropdown/group-field-dropdown.component'
import { HtmlTooltipDirective } from '../../../directives/html-tooltip.directive'
import { InputType } from '../../../enums/input-type.enum'
import { CaseInput } from '../../../interfaces/case-input.interface'
import { ResourceDefinition } from '../../../interfaces/resource-definition.interface'
import { SelectOption } from '../../../interfaces/select-option.interface'
import { CheckboxInputComponent } from '../checkbox-input/checkbox-input.component'
import { ColorPickerInputComponent } from '../color-picker-input/color-picker-input.component'
import { DatepickerInputComponent } from '../datepicker-input/datepicker-input.component'
import { EmailInputComponent } from '../email-input/email-input.component'
import { EurosInputComponent } from '../euros-input/euros-input.component'
import { FileInputComponent } from '../file-input/file-input.component'
import { ImageGalleryInputComponent } from '../image-gallery-input/image-gallery-input.component'
import { ImageInputComponent } from '../image-input/image-input.component'
import { MultiCheckboxInputComponent } from '../multi-checkbox-input/multi-checkbox-input.component'
import { MultiFileInputComponent } from '../multi-file-input/multi-file-input.component'
import { MultiImageInputComponent } from '../multi-image-input/multi-image-input.component'
import { MultiSearchInputComponent } from '../multi-search-input/multi-search-input.component'
import { MultiSelectInputComponent } from '../multi-select-input/multi-select-input.component'
import { MultiTextInputComponent } from '../multi-text-input/multi-text-input.component'
import { NumberInputComponent } from '../number-input/number-input.component'
import { PasswordInputComponent } from '../password-input/password-input.component'
import { RadioInputComponent } from '../radio-input/radio-input.component'
import { RichTextInputComponent } from '../rich-text-input/rich-text-input.component'
import { SelectInputComponent } from '../select-input/select-input.component'
import { TelInputComponent } from '../tel-input/tel-input.component'
import { TextInputComponent } from '../text-input/text-input.component'
import { TextareaInputComponent } from '../textarea-input/textarea-input.component'
import { TimeInputComponent } from '../time-input/time-input.component'
import { ToggleInputComponent } from '../toggle-input/toggle-input.component'

@Component({
    selector: 'case-input',
    templateUrl: './case-input.component.html',
    styleUrls: ['./case-input.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        HtmlTooltipDirective,
        TextInputComponent,
        TextareaInputComponent,
        CheckboxInputComponent,
        ColorPickerInputComponent,
        DatepickerInputComponent,
        EmailInputComponent,
        MultiSearchInputComponent,
        MultiSelectInputComponent,
        NumberInputComponent,
        EurosInputComponent,
        TimeInputComponent,
        RichTextInputComponent,
        SelectInputComponent,
        RadioInputComponent,
        TelInputComponent,
        PasswordInputComponent,
        ToggleInputComponent,
        FileInputComponent,
        ImageInputComponent,
        ImageGalleryInputComponent,
        MultiTextInputComponent,
        MultiImageInputComponent,
        MultiFileInputComponent,
        MultiCheckboxInputComponent,
        GroupFieldDropdownComponent
    ]
})

// * Default ABACUS input wrapper that calls the other ones
export class CaseInputComponent implements CaseInput, OnChanges, OnDestroy {
  // Common props.
  @Input() type: InputType
  @Input() label: string
  @Input() placeholder: string
  @Input() helpText: string
  @Input() showErrors: boolean
  @Input() readonly: boolean
  @Input() validators: ValidatorFn[] = []

  // Input specific props.
  @Input() htmlExample: string
  @Input() initialValue: any
  @Input() forcedValue: any
  @Input() searchResources: ResourceDefinition[]
  @Input() filterResource: ResourceDefinition
  @Input() resourceName: string
  @Input() selectOptions: SelectOption[]
  @Input() step: string
  @Input() accept: string
  @Input() min: number
  @Input() max: number
  @Input() maxSelectedItems: number
  @Input() searchParams: { [key: string]: string }
  @Input() copyDateFromOnDateTo = false
  @Input() secondPlaceholder: string
  @Input() required: boolean
  @Input() showCreateButton = false
  @Input() askFileYear: boolean
  @Input() renderLabelAsHtml = false
  @Input() editorMinHeight?: number
  // optional inputs
  @Input() htmlHelpText?: string
  @Input() htmlTooltip?: string
  @Input() groupBy?: string
  @Input() selectAllDisabled?: boolean
  isInitiativeTypeFieldError = input<boolean>(false)

  @Output() valueChanged: EventEmitter<any> = new EventEmitter()

  InputType = InputType

  public groupedSelectControl: FormControl = new FormControl()
  private valueChangesSubscription: Subscription

  // Generate a unique id for each input. Necessary to make "label -> input" link (click on label to focus input).
  uniqueId: string = Math.floor(Math.random() * 10000).toString()

  // Transmit information to parent.
  onValueChanged(event: any) {
    this.valueChanged.emit(event)
  }

  constructor() {
    this.valueChangesSubscription =
      this.groupedSelectControl.valueChanges.subscribe((value) => {
        if (this.type === InputType.GroupedSelect) {
          this.onValueChanged(value)
        }
      })
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.type === InputType.GroupedSelect) {
      if (
        changes['initialValue'] ||
        changes['forcedValue'] ||
        changes['type']
      ) {
        const valueToSet =
          this.forcedValue !== undefined && this.forcedValue !== null
            ? this.forcedValue
            : this.initialValue !== undefined && this.initialValue !== null
            ? this.initialValue
            : null

        this.groupedSelectControl.setValue(+valueToSet, { emitEvent: false })
      }
    }
  }

  ngOnDestroy() {
    if (this.valueChangesSubscription) {
      this.valueChangesSubscription.unsubscribe()
    }
  }
}
